import Cookies from 'js-cookie'

type Locale = 'enUs' | 'ptBr' | 'esEs' | 'frFr' | 'deDe' | 'itIt'

const LANGUAGE_TO_LOCALE: Partial<Record<string, Locale>> = {
  pt: 'ptBr',
  es: 'esEs',
  fr: 'frFr',
  de: 'deDe',
  it: 'itIt',
  en: 'enUs',
}

const FALLBACK_LOCALE: Locale = 'enUs'

/**
 * Cookie written by the marketing site (signater-site) on the shared
 * `.signater.com` domain. Its value uses BCP-47-ish tags ("pt-br", "en"),
 * so we map by the primary subtag like the browser detection below.
 */
export const SITE_LOCALE_COOKIE = 'signater_locale'

/**
 * Reads the language the user chose on the marketing site, if present.
 *
 * Returns the matching supported locale (e.g. "pt-br" -> "ptBr", "en" -> "enUs")
 * or `null` when the cookie is absent or holds an unsupported value.
 */
export function getSiteLocaleCookie(): Locale | null {
  const value = Cookies.get(SITE_LOCALE_COOKIE)
  if (!value) return null

  const primary = value.split('-')[0].toLowerCase()
  return LANGUAGE_TO_LOCALE[primary] ?? null
}

/**
 * Detects the best matching supported locale from the browser's language preferences.
 *
 * Iterates through `navigator.languages` (or falls back to `navigator.language`)
 * and returns the first match based on the primary language subtag (e.g. "pt" from "pt-PT").
 *
 * Returns `'enUs'` if no supported language is found.
 */
export function detectLocale(): Locale {
  const browserLanguages = navigator.languages.length
    ? navigator.languages
    : [navigator.language]

  for (const lang of browserLanguages) {
    const primary = lang.split('-')[0].toLowerCase()
    const match = LANGUAGE_TO_LOCALE[primary]
    if (match) return match
  }

  return FALLBACK_LOCALE
}
